<template>
    <tailwind-layout title="Maintenance Requests">
        <!-- Initial invite state - no invitations sent and max attempts not reached -->
        <div v-if="$page.props.user.role === 'applicant' && !maintenanceProperties.data.length && !isLandlordInvited && !isInvitationAccepted && !isInvitationExpired && !hasReachedMaxAttempts" class="overflow-hidden h-[calc(100vh-280px)] flex items-center justify-center -mx-4 sm:-mx-6 lg:-mx-8">
            <div class="text-center px-4">
                <h2 class="text-2xl font-bold mb-4">
                    Invite Your Landlord
                </h2>

                <p class="text-lg font-medium text-gray-900 mb-2">
                    Your landlord is not currently signed up to use the Maintenance Portal.
                </p>

                <p class="text-lg font-medium text-gray-900 mb-8">
                    Click below to get started and make Maintenance smoother for everyone!
                </p>

                <button 
                    type="button"
                    @click="showInviteModal = true"
                    class="primaryButton"
                >
                    Invite Landlord
                </button>
            </div>
        </div>
        
        <!-- Active invitation sent state -->
        <div v-else-if="$page.props.user.role === 'applicant' && !maintenanceProperties.data.length && isLandlordInvited && !isInvitationAccepted && !hasReachedMaxAttempts" class="overflow-hidden h-[calc(100vh-280px)] flex items-center justify-center -mx-4 sm:-mx-6 lg:-mx-8">
            <div class="text-center px-4">
                <h2 class="text-2xl font-bold mb-4">
                    Landlord Invitation Sent
                </h2>

                <p class="text-lg font-medium text-gray-900 mb-2">
                    Your landlord has been invited and will receive an email shortly.
                </p>

                <p class="text-sm text-gray-500 mb-8">
                    Attempt {{ totalInvitationAttempts }} of 3
                </p>
            </div>
        </div>
        
        <!-- Invitation expired state - can send another if under limit -->
        <div v-else-if="$page.props.user.role === 'applicant' && !maintenanceProperties.data.length && isInvitationExpired && !hasReachedMaxAttempts" class="bg-white p-6 rounded-lg shadow-lg mb-6">
            <div class="flex flex-col items-center text-center space-y-4">
                <div class="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                    <svg class="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900">Invitation Expired</h3>
                <p class="text-gray-600 text-center px-4">
                    Your previous invitation to the landlord has expired after 7 days.
                    <br><br>
                    You can send another invitation if needed.
                    <br><br>
                    <span class="text-sm text-gray-500">Attempt {{ totalInvitationAttempts }} of 3 used</span>
                    <br><br>
                    <button @click="showInviteModal = true" class="primaryButton">
                        Send Another Invitation
                    </button>
                </p>
            </div>
        </div>
        
        <!-- Maximum attempts reached state -->
        <div v-else-if="$page.props.user.role === 'applicant' && !maintenanceProperties.data.length && hasReachedMaxAttempts && !isInvitationAccepted" class="bg-white p-6 rounded-lg shadow-lg mb-6">
            <div class="flex flex-col items-center text-center space-y-4">
                <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"/>
                    </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900">Maximum Invitations Sent</h3>
                <p class="text-gray-600 text-center px-4">
                    You have reached the maximum number of invitation attempts (3).
                    <br><br>
                    If you need further assistance, please contact our support team.
                    <br><br>
                    <span class="text-sm text-gray-500">All 3 invitation attempts have been used</span>
                </p>
            </div>
        </div>
        <div v-else>
            <div class="overflow-hidden">
                <div
                v-for="(maintenanceProperty, index) in maintenanceProperties.data" :key="index"
                class="pt-5 pb-0 sm:pb-5 overflow-hidden sm:rounded-lg mb-2">
                <div>
                    <div class="pb-4 mx-auto hidden md:flex justify-between items-center"><!-- Block for lg and larger -->
                        <h1 class="font-semibold text-2xl leading-tight">Maintenance Property</h1>
                        <div class="flex items-center space-x-4">
                            <span v-if="$page.props.user.role === 'applicant'" class="isolate inline-flex rounded-md shadow-sm">
                                <inertia-link :href="route('maintenance-requests.create', maintenanceProperty.property.id)"
                                            class="primaryButton">
                                    <span class="">New Request</span>
                                </inertia-link>
                                <!-- <button @click="showInviteModal = true" class="primaryButton ml-5">
                                    Invite Landlord
                                </button> -->
                            </span>
                        </div>
                    </div>

                    <div class="mb-3 md:px-8 mx-auto flex flex-col md:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens)-->
                        <div class="flex justify-between items-start md:items-center">
                            <div class="flex flex-col">
                                <h1 class="font-semibold text-2xl leading-tight">Maintenance Property</h1>
                                <span v-if="$page.props.user.role === 'applicant'" class="isolate inline-flex my-4">
                                    <inertia-link :href="route('maintenance-requests.create', maintenanceProperty.property.id)"
                                                class="primaryButton">
                                        <span class="">New Request</span>
                                    </inertia-link>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <div class="bg-white overflow-hidden rounded-lg p-6 mb-2">

                    <div class="sm:flex flex-row justify-between border-b mb-4">
                        <h1 class="text-lg font-bold text-black">{{ maintenanceProperty.property.full_address }}</h1>
                        <h2>
                            <span class="text-lg font-bold text-black">Landlord:</span>
                            {{ getOwnerName(maintenanceProperty) }}
                        </h2>
                        </div>
                        <div class="sm:flex flex-row gap-x-4">
                            <!-- First Column - Warranty Company Info -->
                            <div
                                v-if="getTenantProperty(maintenanceProperty)?.tenant_can_contact_directly === 1"
                                class="bg-gray-200 p-4 rounded-lg basis-1/3">
                                <h2 class="text-lg font-bold text-black">Warranty Company Info</h2>
                                <ul v-if="getTenantProperty(maintenanceProperty)?.tenant_can_contact_directly === 1">
                                    <li v-if="getTenantProperty(maintenanceProperty)?.warranty_company_name"><span class="font-bold">Company Name: </span>{{ getTenantProperty(maintenanceProperty)?.warranty_company_name || 'N/A' }}</li>
                                    <li v-if="getTenantProperty(maintenanceProperty)?.company_phone"><span class="font-bold">Phone: </span> <a :href="'tel:' + getTenantProperty(maintenanceProperty)?.company_phone" class="underline text-blue-600">{{ formatPhoneNumber(getTenantProperty(maintenanceProperty)?.company_phone) || 'N/A' }}</a></li>
                                    <li v-if="getTenantProperty(maintenanceProperty)?.company_website"><span class="font-bold">Website: </span> <a :href="getTenantProperty(maintenanceProperty)?.company_website" target="_blank" class="underline text-blue-600">{{ getTenantProperty(maintenanceProperty)?.warranty_company_name || 'N/A' }}</a></li>
                                    <li v-if="getTenantProperty(maintenanceProperty)?.contract_number"><span class="font-bold">Contract #: </span>{{ getTenantProperty(maintenanceProperty)?.contract_number || 'N/A' }}</li>
                                    <li v-if="!getTenantProperty(maintenanceProperty)?.warranty_company_name && !getTenantProperty(maintenanceProperty)?.company_phone && !getTenantProperty(maintenanceProperty)?.company_website && !getTenantProperty(maintenanceProperty)?.contract_number">N/A</li>
                                </ul>
                                <p v-else class="text-gray-500"></p>
                            </div>

                            <!-- Second Column - Specific Instructions -->
                            <div class="mt-4 sm:mt-0 basis-1/3" v-if="getTenantProperty(maintenanceProperty)?.specific_instructions">
                                <h2 class="text-md font-bold text-black">Specific Instructions</h2>
                                <p class="text-sm">{{ getTenantProperty(maintenanceProperty)?.specific_instructions }}</p>
                            </div>

                            <!-- Third Column - Emergency Guidelines -->
                            <div class="mt-4 sm:mt-0 basis-1/3" v-if="getTenantProperty(maintenanceProperty)?.guidelines">
                                <h2 class="text-md font-bold text-black">Emergency Guidelines</h2>
                                <p class="text-sm">{{ getTenantProperty(maintenanceProperty)?.guidelines }}</p>
                            </div>
                        </div>
                        <div class="mt-4">
                            <div v-if="getTenantProperty(maintenanceProperty)?.repair_amount" class="inline-flex">
                                <h2 class="text-md font-bold text-black mr-2">Tenant is responsible for any repairs up to: </h2>
                                <p class="text-md">${{ getTenantProperty(maintenanceProperty)?.repair_amount }}</p>
                            </div>
                        </div>
                    </div>

                </div>
                </div>
                <div v-show="!maintenanceProperties.data.length" class="px-4 py-5 bg-white bg-rent-risk-blue-100 overflow-hidden sm:rounded-lg p-6 max-w-7xl mb-2">
                    <div>
                        No Maintenance Properties were found.
                    </div>
                </div>
            </div>
            <alert-card v-if="$page.props.user.role === 'applicant'" type="info" class="my-3">
                <span>
                    If you are experiencing any issues with this property, please submit a maintenance request by providing a clear description of the problem. Be as specific as possible to help the maintenance team resolve the issue quickly. After submitting, you will receive updates regarding the status of your request.
                </span>
            </alert-card>

            <div class="overflow-hidden">
                <div class="relative">
                    <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch sm:ml-2 lg:ml-0">
                        <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1 px-2">
                            <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of requests per page">
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                            <input id="search" v-model="form.search" type="text" placeholder="Search for maintenance requests..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md sm:mx-2">
                            <div class="flex items-center mt-2 md:ml-2" @click="show_archived_requests()">
                                <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                    <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400']">
                                        <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                    </Switch>
                                    <SwitchLabel as="span" class="ml-3 text-sm">
                                        <span class="font-medium text-gray-700">Show Archived Requests</span>
                                    </SwitchLabel>
                                </SwitchGroup>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                    <div class="flex flex-col">
                        <div class="overflow-x-auto">
                            <div class="align-middle inline-block min-w-full">
                                <div class="shadow overflow-hidden lg:border">
                                    <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                    <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                    <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                        <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left cursor-pointer">
                                                <span @click="sortTable('emergency_types_id')">
                                                    Request Type
                                                    <icon v-show="form.sort_by === 'emergency_types_id'" :name="form.sort_direction_icon" />
                                                    <icon v-if="!form.sort_by || form.sort_by !== 'emergency_types_id'" :name="'sort'" />
                                                </span>
                                            </th>
                                            <th scope="col" width="30%" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Description
                                            </th>
                                            <th v-if="$page.props.user.role === 'agent'" scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Receipts
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Attachments
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left cursor-pointer">
                                                <span @click="sortTable('created_at')">
                                                    Created at
                                                    <icon v-show="form.sort_by === 'created_at'" :name="form.sort_direction_icon" />
                                                    <icon v-if="!form.sort_by || form.sort_by !== 'created_at'" :name="'sort'" />
                                                </span>
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Status
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">

                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200">
                                        <tr v-for="(maintenanceRequest, index) in maintenanceRequests.data" :key="index"
                                            :class="[(index % 2 === 0 && !maintenanceRequest.deleted_at ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : (!maintenanceRequest.deleted_at) ? 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer' : ''),
                                            (maintenanceRequest.deleted_at ? 'bg-red-100 !border-b !border-red-200 hover:bg-red-200 hover:cursor-pointer' : '')]"
                                            @click="show_property_page(maintenanceRequest)"
                                        >
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ maintenanceRequest.emergency_type_name ?? 'Other' }}
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ maintenanceRequest.other_description }}
                                            </td>
                                            <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-3 pr-3 text-sm font-medium text-gray-900">
                                                <button
                                                    v-if="!maintenanceRequest.deleted_at"
                                                    @click.stop="showReceipts(maintenanceRequest)"
                                                    class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                >
                                                    Receipts
                                                </button>
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <div v-if="maintenanceRequest.images && maintenanceRequest.images.length > 0" class="flex space-x-4">
                                                <button @click="viewAllAttachments(maintenanceRequest)" class="font-medium text-indigo-600 hover:text-indigo-500" @click.native.stop>View All</button>
                                                <button @click="downloadAllAttachments(maintenanceRequest)" class="font-medium text-indigo-600 hover:text-indigo-500" @click.native.stop>Download All</button>
                                                </div>
                                            </td>
                                            <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ maintenanceRequest.created_at }}
                                            </td>
                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                <span v-if="!maintenanceRequest.deleted_at" :class="getStatusColor(maintenanceRequest.status)">
                                                    {{ maintenanceRequest.status }}
                                                </span>
                                                <span v-if="maintenanceRequest.deleted_at" class="text-red-700 bg-red-50 ring-red-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset">
                                                    Archived
                                                </span>
                                            </td>
                                            <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                                <!-- <inertia-link v-if="!maintenanceRequest.deleted_at" :href="route('maintenance-requests.show', [property.id, maintenanceRequest.id])"
                                                    class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                    View
                                                </inertia-link> -->
                                                <button v-if="maintenanceRequest.deleted_at && $page.props.user.role === 'agent'"
                                                    title="Restore Request"
                                                    @click="restoreRequest(maintenanceRequest)"
                                                    class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                    Restore
                                                </button>

                                            </td>
                                        </tr>
                                        <tr v-show="!maintenanceRequests.data.length">
                                            <td colspan="5" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                                No maintenance requests were found.
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>

                                    <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                    <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                        <li v-for="(maintenanceRequest, index) in maintenanceRequests.data" :key="index" :class="[maintenanceRequest.deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
                                            <inertia-link :href="route('maintenance-requests.show', [maintenanceRequest.property_id, maintenanceRequest.id])">
                                            <div class="flex flex-col p-6">
                                                <h3 class="truncate text-sm font-medium text-gray-900">
                                                    {{ maintenanceRequest.emergency_type_name ?? 'Other' }}
                                                    <span
                                                    v-if="maintenanceRequest.deleted_at"
                                                    class="inline-block text-red-800 bg-red-100 border border-red-300 rounded-full px-3 py-0.5 text-xs font-semibold"
                                                    >
                                                    Archived
                                                    </span>
                                                </h3>
                                                <h3 v-if="maintenanceRequest.other_description" class="truncate text-sm font-medium text-gray-900">{{ maintenanceRequest.other_description }}</h3>
                                                <h3 class="truncate text-sm font-medium text-gray-900 flex gap-2">Attachments:
                                                    <div>
                                                        <ol class="list-inside text-sm">
                                                            <li v-for="(image, key) in maintenanceRequest.images" :key="key">
                                                                <a :href="route('maintenance-requests.view-attachment', [maintenanceRequest.property_id, image.id])" target="_blank" class="font-medium text-indigo-600 hover:text-indigo-500" @click.native.stop>View</a>
                                                                <a :href="route('maintenance-requests.download-attachment', [maintenanceRequest.property_id, image.id])" target="_blank" class="font-medium text-indigo-600 hover:text-indigo-500 ml-4" @click.native.stop>Download</a>
                                                            </li>
                                                        </ol>
                                                    </div>
                                                </h3>
                                                <h3 class="truncate text-sm font-medium text-gray-900">Created At: {{ maintenanceRequest.created_at }}</h3>
                                                <h3 class="truncate text-sm font-medium text-gray-900">
                                                    <span v-if="!maintenanceRequest.deleted_at">
                                                        Status: {{ maintenanceRequest.status }}
                                                    </span>

                                                </h3>
                                            </div>
                                            </inertia-link>
                                            <div>
                                                <div :class="[maintenanceRequest.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                    <div class="flex w-0 flex-1">
                                                        <inertia-link :href="route('maintenance-requests.show', [maintenanceRequest.property_id, maintenanceRequest.id])" :class="[maintenanceRequest.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                            <font-awesome-icon icon="fa-eyes" />
                                                            View
                                                        </inertia-link>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <pagination class="mt-6" :links="maintenanceRequests.links" :results="maintenanceRequests" :type="'Maintenance Requests'" />
            </div>
            <div v-if="showRestoreModal">
                <TransitionRoot as="template" :show="showRestoreModal">
                    <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </TransitionChild>

                        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                    <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                        <div>
                                            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                                <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                            </div>
                                            <div class="mt-3 text-center sm:mt-5">
                                                <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this request?</DialogTitle>
                                                <div class="mt-2">
                                                </div>
                                            </div>
                                        </div>
                                        <div class="mt-5 sm:mt-6 flex">
                                            <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                            <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="restore()">Confirm</button>
                                        </div>
                                    </DialogPanel>
                                </TransitionChild>
                            </div>
                        </div>
                    </Dialog>
                </TransitionRoot>
            </div>
    </div>
        <!-- Receipts -->
        <modal-jetstream :show="showReceiptsModal" :max-width="'4xl'">
            <div class="overflow-x-auto m-4">

                <!-- Header -->
                <div class="max-w-3xl mx-auto mt-8 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                    <h2 class="text-xl font-bold">Add New Receipt</h2>
                </div>

                <!-- Form -->
                    <form @submit.prevent="store" class="space-y-4">
                        <div class="space-y-2 divide-y divide-gray-200">
                            <div class="mt-6 grid grid-cols-3 gap-y-4 gap-x-4 sm:grid-cols-6">

                                <!-- Receipt -->
                                <div class="sm:col-span-2">
                                    <label for="receipt" :class="receiptForm.errors.receipt ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Receipt <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="file" @input="receiptForm.receipt = $event.target.files[0]" id="receipt"
                                            :class="receiptForm.errors.receipt ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                        <p v-if="receiptForm.errors.receipt" class="text-red-500 text-sm">{{ receiptForm.errors.receipt }}</p>
                                    </div>
                                </div>

                                <!-- Price -->
                                <div class="sm:col-span-2">
                                    <label for="price" :class="receiptForm.errors.price ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Price <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="number" v-model="receiptForm.price" id="price" step="0.01"
                                            :class="receiptForm.errors.price ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                        <p v-if="receiptForm.errors.price" class="text-red-500 text-sm">{{ receiptForm.errors.price }}</p>
                                    </div>
                                </div>

                                <!-- Vendor Name -->
                                <div class="sm:col-span-2">
                                    <label for="vendor_name" :class="receiptForm.errors.vendor_name ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Vendor Name <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="text" v-model="receiptForm.vendor_name" id="vendor_name"
                                            :class="receiptForm.errors.vendor_name ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                        <p v-if="receiptForm.errors.vendor_name" class="text-red-500 text-sm">{{ receiptForm.errors.vendor_name }}</p>
                                    </div>
                                </div>
                                <div class="sm:col-span-2">
                                    <label for="date" :class="receiptForm.errors.date ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Date <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="date" v-model="receiptForm.date" id="date"
                                            :class="receiptForm.errors.date ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                        <p v-if="receiptForm.errors.date" class="text-red-500 text-sm">{{ receiptForm.errors.vendor_name }}</p>
                                    </div>
                                </div>

                                <!-- Notes -->
                                <div class="sm:col-span-4">
                                    <label for="notes" :class="receiptForm.errors.notes ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Notes
                                    </label>
                                    <div class="mt-1">
                                        <textarea id="notes" v-model="receiptForm.notes" rows="2"
                                            :class="receiptForm.errors.notes ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block sm:text-sm border p-2 rounded-md"></textarea>
                                        <p v-if="receiptForm.errors.notes" class="text-red-500 text-sm">{{ receiptForm.errors.notes }}</p>
                                    </div>
                                </div>

                            </div>
                        </div>

                        <!-- Submit Button -->
                        <div class="text-right pr-2 flex justify-end">
                            <button @click="showReceiptsModal = false"
                                class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400 mr-2">
                                Cancel
                            </button>
                            <form-button :form="receiptForm" type="submit">
                                Save
                            </form-button>
                        </div>
                    </form>
                <div>
                    <!-- Previous Receipts Header -->
                    <div class="max-w-3xl mx-auto mt-4 mb-4 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                        <h2 class="text-xl font-bold">Previous Receipts</h2>
                    </div>

                    <!-- Receipts Table -->
                    <div class="flex flex-col">
                        <div class="overflow-x-auto">
                            <div class="align-middle inline-block min-w-full">
                                <div class="shadow overflow-hidden lg:border">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead class="bg-blue-50">
                                            <tr>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Date Uploaded
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Price
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Vendor Name
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Notes
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(receipt, index) in selectedRequest.receipts" :key="index"
                                                class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer">
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ receipt.formatted_date }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    ${{ receipt.formatted_price }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ receipt.vendor_name }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ receipt.notes }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900 flex space-x-4">
                                                    <a target="_blank" :href="route('maintenance-requests.view-receipt', receipt.id)"
                                                        class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
                                                    <a target="_blank" :href="route('maintenance-requests.download-receipt', receipt.id)"
                                                        class="font-medium text-indigo-600 hover:text-indigo-500">Download</a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

    <!-- Close Button -->
    <button @click="showReceiptsModal = false"
        class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">
        Close
    </button>

</modal-jetstream>
<div v-if="showInviteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg w-full max-w-md p-6 relative">
        <!-- Close button -->
        <button @click="showInviteModal = false" class="absolute top-4 right-4 text-gray-400 hover:text-gray-500">
            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>

        <h2 class="text-xl font-bold mb-4">Invite Landlord</h2>

        <form @submit.prevent="sendInvitation" class="space-y-4">
            <div>
                <label for="landlord_email" class="block text-sm font-medium text-gray-700 mb-1">
                    Landlord's Email Address <span class="text-red-600">*</span>
                </label>
                <div class="mt-1">
                    <input type="email" id="landlord_email" v-model="inviteForm.landlord_email" required
                           class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                           :class="{ 'border-red-500': inviteForm.errors.landlord_email }" />
                    <p v-if="inviteForm.errors.landlord_email" class="mt-2 text-sm text-red-600">
                        {{ inviteForm.errors.landlord_email }}
                    </p>
                </div>
            </div>

            <div class="flex justify-end space-x-3">
                <button type="button" @click="onCancel" class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    Cancel
                </button>
                <button :disabled="isInviteButtonDisabled" type="submit" :class="isInviteButtonDisabled ? 'px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-not-allowed':'px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer'">
                    Send Invitation
                </button>
            </div>
        </form>
    </div>
</div>

    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import AlertCard from "@/Shared/AlertCard.vue";
import ModalJetstream from "@/Jetstream/Modal.vue";
import FormButton from '@/Shared/FormButton.vue';
import {Switch, SwitchLabel, SwitchGroup} from "@headlessui/vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faPlus, faArrowsRotate, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { router } from '@inertiajs/vue3'

export default {
    components: {
        SwitchGroup,
        SwitchLabel,
        Switch,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesomeIcon,
        AlertCard,
        ModalJetstream,
        FormButton
    },
    props: {
        filters: Object,
        maintenanceProperties: Object,
        maintenanceTenantProperty: Object,
        maintenanceRequests: Object,
        user: Object,
        isLandlordInvited: Boolean,
        isInvitationAccepted: Boolean,
        isInvitationExpired: Boolean,
        hasReachedMaxAttempts: Boolean,
        totalInvitationAttempts: Number
    },
    data() {
        return {
            form: {
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:25,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            showRestoreModal: false,
            selectedRequest: null,
            showReceiptsModal: false,
            isInviteButtonDisabled: false,

            receiptForm: this.$inertia.form({
                receipt: null,
                price: null,
                vendor_name: null,
                date: null,
                notes: null,
            }),
            showInviteModal: false,
            inviteForm: this.$inertia.form({
                landlord_email: ''
            }),
            faChevronLeft,
            faPlus,
            faArrowsRotate,
            faCircleCheck,
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('maintenance-properties.index'), pickBy(this.form), { preserveState: true, preserveScroll: true })
            }, 150),
        },
    },
    computed: {
        getTenantProperty() {
            return (maintenanceProperty) => {
                return this.maintenanceTenantProperty.find(p => p.id === maintenanceProperty.id)
            }
        },
        getOwnerName() {
            return (property) => {
                return this.maintenanceTenantProperty.find(p => p.id === property.id)?.property.owner_name || 'N/A'
            }
        },
        formatPhoneNumber() {
            return (phoneNumber) => {
                if (!phoneNumber) return phoneNumber;
                const cleaned = ('' + phoneNumber).replace(/\D/g, '');
                const match = cleaned.match(/^([0-9]{3})([0-9]{3})([0-9]{4})$/);
                if (match) {
                    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
                }
                return null;
            }
        }
    },
    methods: {
        show_property_page(maintenanceRequest) {
            if(maintenanceRequest.deleted_at === null) {
                this.$inertia.get(this.route('maintenance-requests.show', {maintenance_property: maintenanceRequest.property_id, maintenance_request: maintenanceRequest.id}));
            }
        },
        show_archived_requests() {
            this.form.trashed = !this.form.trashed;
        },
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },

        restoreRequest(maintenanceRequest) {
            this.selectedRequest = maintenanceRequest;
            this.showRestoreModal = true;
        },
        restore() {
            this.$inertia.put(this.route('maintenance-requests.restore', {maintenance_property: this.selectedRequest.property_id, maintenance_request: this.selectedRequest.id}), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },
        viewAllAttachments(maintenanceRequest) {
          maintenanceRequest.images.forEach((image, index) => {
            setTimeout(() => {
              window.open(
                this.route('maintenance-requests.view-attachment', [maintenanceRequest.property_id, image.id]),
                '_blank'
              );
            }, index * 300); // Add small delay between each window
          });
        },
        downloadAllAttachments(maintenanceRequest) {
          maintenanceRequest.images.forEach((image, index) => {
            setTimeout(() => {
              // Create a hidden anchor element
              const link = document.createElement('a');
              link.href = this.route('maintenance-requests.download-attachment', [maintenanceRequest.property_id, image.id]);
              link.target = '_blank';
              link.download = '';

              // Append to body and trigger click
              document.body.appendChild(link);
              link.click();

              // Clean up
              document.body.removeChild(link);
            }, index * 300); // Add small delay between each download
          });
        },

        showReceipts(maintenanceRequest) {
            this.selectedRequest = maintenanceRequest;
            this.showReceiptsModal = true;
        },
        store() {
            var selectedRequestId = this.selectedRequest.id;
            this.receiptForm.post(this.route('maintenance-requests.store-receipts', this.selectedRequest.id), {
                preserveState: true,
                onSuccess: () => {
                    const requestsArray = Object.values(this.maintenanceProperties.data.maintenanceRequests);
                    this.selectedRequest = requestsArray.find(request => request.id === selectedRequestId) || null;

                    this.receiptForm.reset();
                },
                onError: (errors) => {
                    this.receiptForm.errors = errors;
                }
            });
        },
        getStatusColor(status) {
            const colors = {
                'Reported': 'text-yellow-700 bg-yellow-50 ring-yellow-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
                'In progress': 'text-blue-700 bg-blue-50 ring-blue-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
                'Closed': 'text-green-700 bg-green-50 ring-green-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
            };
            return colors[status] || 'text-green-700 bg-green-50 ring-green-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset';
        },
        sendInvitation() {
            this.isInviteButtonDisabled = true;
            this.inviteForm.post(this.route('landlords.invite'), {
                preserveScroll: true,
                onSuccess: () => {
                    this.inviteForm.reset();
                    this.showInviteModal = false;

                },
                onError: (errors) => {
                    this.inviteForm.errors = errors;
                    this.showInviteModal = true;
                },
                onFinish: () => {
                    this.isInviteButtonDisabled = false;
                }
            });


        },
        onCancel() {
            this.inviteForm.reset();
            this.showInviteModal = false;
            this.inviteForm.errors = {};
        },
    },
}
</script>
